"use client";

import Img from '@/components/Img';
import Image from "next/image";
import Link from "next/link";
import Logo from "./Logo";
import FeedbackLink from "./FeedbackLink";
import type { SiteSettings } from "@/lib/fleet-server";
import { t as tr, localeHref } from "@/lib/locale-utils";
import { useLocale } from "@/lib/locale-context";
import { track } from "@/lib/metrika";

const FALLBACK_LEGAL =
  "ООО «Урал Кар Рентал»\nИНН/КПП 2540160475 / 254301001\nОГРН 1102540001431\nЮридический адрес: 690074, Приморский край, г. Владивосток, ул. Снеговая, д. 34, офис 205";
const FALLBACK_LEGAL_EN =
  "LLC «Ural Car Rental»\nINN/KPP 2540160475 / 254301001\nOGRN 1102540001431\nLegal address: 690074, Primorsky Krai, Vladivostok, Snegovaya St., 34, office 205";

const INFO_LINKS = [
  { href: "/news", key: "nav.news" },
  { href: "/blog", key: "nav.blog" },
  { href: "/corporate", key: "nav.corporate" },
  { href: "/conditions", key: "nav.conditions" },
  { href: "/prices", key: "nav.prices" },
  { href: "/documents", key: "nav.documents" },
  { href: "/about/faq", key: "nav.faq" },
  { href: "/feedback", key: "nav.feedback" },
  { href: "/support", key: "nav.support" },
  { href: "/contacts", key: "nav.contacts" },
];

// Ведут на страницу города /{slug}. slug — реальный из rent_site_cities
// (не транслит названия: Москва→moscow, СПб→spb, Алтай→altai и т.п.).
// Должен совпадать с полным списком городов rent_site_cities — иначе в футере
// «не все» города (или битые ссылки на 404, как был /krasnodar).
const STATIONS: { name: string; name_en: string; slug: string }[] = [
  { name: "Москва", name_en: "Moscow", slug: "moscow" },
  { name: "Санкт-Петербург", name_en: "Saint Petersburg", slug: "spb" },
  { name: "Сочи", name_en: "Sochi", slug: "sochi" },
  { name: "Геленджик", name_en: "Gelendzhik", slug: "gelendzhik" },
  { name: "Крым", name_en: "Crimea", slug: "crimea" },
  { name: "Казань", name_en: "Kazan", slug: "kazan" },
  { name: "Екатеринбург", name_en: "Yekaterinburg", slug: "ekaterinburg" },
  { name: "Челябинск", name_en: "Chelyabinsk", slug: "chelyabinsk" },
  { name: "Красноярск", name_en: "Krasnoyarsk", slug: "krasnoyarsk" },
  { name: "Алтай", name_en: "Altai", slug: "altai" },
  { name: "Владивосток", name_en: "Vladivostok", slug: "vladivostok" },
  { name: "Калининград", name_en: "Kaliningrad", slug: "kaliningrad" },
  { name: "Пхукет", name_en: "Phuket", slug: "phuket" },
];

const LEGAL = [
  { href: "/legal/offer", key: "legal.offer" },
  { href: "/legal/rentalterms", key: "legal.rentalterms" },
  { href: "/legal/privacy", key: "legal.privacy" },
  { href: "/legal/personal-data", key: "legal.personal" },
];

// full: иконка-бейдж со своим фоном (MAX) — без кружка и без перекраски в белое.
const SOCIALS: { name: string; bg: string; icon: string; full?: boolean }[] = [
  { name: "vk", bg: "#0077ff", icon: "/icons/social/vk.svg" },
  { name: "tg", bg: "#26a5e4", icon: "/icons/social/tg.svg" },
  { name: "ok", bg: "#ee8208", icon: "/icons/social/ok.svg" },
  { name: "yt", bg: "#ff0000", icon: "/icons/social/yt.svg" },
  { name: "rutube", bg: "#100943", icon: "/icons/social/rutube.svg" },
  { name: "tiktok", bg: "#000000", icon: "/icons/social/tiktok.svg" },
  { name: "wa", bg: "#25d366", icon: "/icons/social/wa.svg" },
  { name: "max", bg: "transparent", icon: "/icons/social/max.svg", full: true },
];

export default function Footer({ contacts }: { contacts?: SiteSettings["contacts"] }) {
  const { locale, dict } = useLocale();
  const t = (k: string) => tr(dict, k);
  const href = (p: string) => localeHref(p, locale);
  const c = contacts as (Record<string, string> | undefined);
  const phone = contacts?.phone || "8 800 444-48-48";
  const phoneTel = phone.replace(/[^+\d]/g, "");
  const email = contacts?.email || "";
  const workingHours = (locale === "en" && c?.working_hours_en) ? c.working_hours_en : (contacts?.working_hours_ru || t("footer.hours"));
  const socials = contacts?.socials || {};
  const legalSrc = locale === "en" ? (c?.legal_en || FALLBACK_LEGAL_EN) : (contacts?.legal_ru || FALLBACK_LEGAL);
  const legalLines = legalSrc.split("\n").map((s) => s.trim()).filter(Boolean);
  return (
    <footer className="bg-bg pt-12 pb-8 lg:pt-[48px] lg:pb-[32px]">
      <div className="mx-auto w-full max-w-[1200px] px-5 sm:px-8 xl:px-0">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {/* col 1 — schedule + phone + socials + brand */}
          <div className="flex flex-col gap-4">
            <Logo variant="dark" />
            <div className="flex flex-col gap-4 text-black">
              <p className="text-[20px] font-semibold leading-tight">{t("footer.schedule")}</p>
              <p className="text-[16px] leading-6">{workingHours}</p>
            </div>
            <a
              href={`tel:${phoneTel}`}
              onClick={() => track("phone_click")}
              className="inline-flex h-11 w-fit items-center rounded-lg bg-primary-soft px-4 text-[16px] leading-6 text-text-on-dark transition hover:bg-primary"
            >
              {phone}
            </a>
            {email && (
              <a href={`mailto:${email}`} onClick={() => track("email_click")} className="inline-flex w-fit items-center gap-2 text-[16px] leading-6 text-black transition hover:text-primary">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 7-8.99 5.73a2 2 0 0 1-2.01 0L2 7" /><rect x="2" y="4" width="20" height="16" rx="2" /></svg>
                {email}
              </a>
            )}
            <div className="flex flex-wrap gap-2">
              {SOCIALS.map((s) => {
                const href = (socials as Record<string, string>)[s.name];
                if (!href) return null; // показываем иконку только если задана ссылка
                return (
                  <a
                    key={s.name}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    onClick={() => track(s.name === "wa" ? "whatsapp_click" : s.name === "tg" ? "telegram_click" : "social_click")}
                    className="flex size-10 items-center justify-center overflow-hidden rounded-full transition hover:opacity-90"
                    style={{ background: s.bg }}
                  >
                    {s.full
                      ? <Img src={s.icon} alt={s.name} width={40} height={40} className="size-10 object-contain" />
                      : <Img src={s.icon} alt={s.name} width={20} height={20} className="size-5" style={{ filter: "brightness(0) invert(1)" }} />}
                  </a>
                );
              })}
            </div>
          </div>

          {/* col 2 — information */}
          <div className="flex flex-col gap-4">
            <p className="text-[20px] font-semibold leading-tight text-black">{t("footer.info")}</p>
            <nav className="flex flex-col gap-2">
              {INFO_LINKS.map((l) =>
                l.href === "/feedback" ? (
                  <FeedbackLink key={l.href} className="text-[16px] leading-6 text-primary transition hover:underline">
                    {t(l.key)}
                  </FeedbackLink>
                ) : (
                  <Link
                    key={l.href}
                    href={href(l.href)}
                    className="text-[16px] leading-6 text-primary transition hover:underline"
                  >
                    {t(l.key)}
                  </Link>
                ),
              )}
            </nav>
          </div>

          {/* col 3 — stations */}
          <div className="flex flex-col gap-4">
            <p className="text-[20px] font-semibold leading-tight text-black">{t("footer.stations")}</p>
            <nav className="flex flex-col gap-2">
              {STATIONS.map((s) => (
                <Link
                  key={s.slug}
                  href={href(`/${s.slug}`)}
                  className="text-[16px] leading-6 text-primary transition hover:underline"
                >
                  {locale === "en" ? s.name_en : s.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* col 4 — contacts */}
          <div className="flex flex-col gap-4">
            <p className="text-[20px] font-semibold leading-tight text-black">{t("footer.contacts")}</p>
            <div className="space-y-1 text-[16px] leading-6 text-black">
              {legalLines.map((line, i) => <p key={i}>{line}</p>)}
            </div>
            <nav className="mt-2 flex flex-col gap-2">
              {LEGAL.map((l) => (
                <Link key={l.href} href={href(l.href)} className="text-[16px] leading-6 text-primary transition hover:underline">
                  {t(l.key)}
                </Link>
              ))}
            </nav>
          </div>
        </div>

        <div className="mt-10 border-t border-black/10" />

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <p className="text-[16px] font-semibold text-text-muted">{t("footer.copyright")}</p>
          {/* payment methods */}
          <div className="flex items-center gap-4 text-text-muted">
            <span className="text-[18px] font-bold italic text-[#1a1f71]">VISA</span>
            <span className="flex items-center">
              <span className="size-6 rounded-full bg-[#eb001b]" />
              <span className="-ml-2 size-6 rounded-full bg-[#f79e1b] mix-blend-multiply" />
            </span>
            <span className="text-[16px] font-bold text-[#0f754e]">{locale === "en" ? "MIR" : "МИР"}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
